/**
 * PaywallAPI implementation (plan §5.8). present(gate) opens PremiumGateCard full screen
 * (centred sheet ≥ 768 px); purchases go through Stripe Checkout (full-page redirect) and are
 * completed on return: `?checkout=success` → poll premium_entitlements every 2 s up to 60 s,
 * then onEntitled; `?checkout=cancel` → native_paywall_dismissed, retention signal
 * purchase_cancelled and the exit offer (once per session) — iOS parity.
 * present() tracks `premium_gate_hit` (once per presentation, skipped when premium); the card tracks native_paywall_*.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/app/providers';
import type { PremiumGate } from '@/domain';
import { track } from '@/services/analytics';
import { api, type RetentionEvaluation, type RetentionPlacement, type RetentionSignal } from '@/services/api';
import { isPremium as fetchIsPremium } from '@/services/progressRepository';
import { preloadExitGift } from './lottie';
import { clearIntent, exitOfferShownThisSession, markRetentionPromptShown, parseCheckoutReturn, readIntent, retentionPromptShownThisSession, startCheckout, type CheckoutIntent } from './checkoutFlow';
import { isPaywallVariant, VARIANT_FOR_GATE, type PaywallVariant } from './copy';
import { hasEverSubscribed as fetchHasEverSubscribed, pollUntil } from './entitlement';
import { usePaywallPrices } from './usePaywallPrices';
import { PaywallContext, type PaywallAPI, type PresentOptions } from './usePaywall';
import { RetentionContext, type RetentionAPI } from './useRetentionOffer';

// Lazy chunks (motion + card + heroes): off the first-load path, prefetched once the app is idle.
const loadOverlay = () => import('./PaywallOverlay');
const PaywallOverlay = lazy(loadOverlay);
const RetentionOfferSheet = lazy(() => import('./RetentionOffer').then((m) => ({ default: m.RetentionOfferSheet })));

interface Presentation { key: number; gate?: PremiumGate; variant: PaywallVariant; options?: PresentOptions; canDismiss: boolean; startInExit: boolean }

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

export function PaywallProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const { auth, isPremium, setPremium, t, locale } = session;
  const navigate = useNavigate();
  const location = useLocation();
  const [returnInfo] = useState(() => {
    const r = parseCheckoutReturn(typeof window === 'undefined' ? '' : window.location.search);
    return r.outcome ? { outcome: r.outcome, intent: readIntent(), cleanedSearch: r.cleanedSearch } : undefined;
  });
  const [presentation, setPresentation] = useState<Presentation>();
  const [confirming, setConfirming] = useState(returnInfo?.outcome === 'success');
  const [notice, setNotice] = useState<string>();
  const [everSubscribed, setEverSubscribed] = useState(false);
  const [retention, setRetention] = useState<RetentionEvaluation>();
  const [retentionPrompt, setRetentionPrompt] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string>();
  const confirmingRef = useRef(confirming);
  const pendingRef = useRef<PresentOptions[]>([]);
  const keyRef = useRef(0);
  const prices = usePaywallPrices();
  // Mounted from the first presentation on (keeps AnimatePresence alive for the exit animation).
  const [overlayMounted, setOverlayMounted] = useState(false);
  if (presentation && !overlayMounted) setOverlayMounted(true);
  // Prefetch the card chunk when the browser is idle so the first tap opens it without a wait.
  useEffect(() => {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
    const prefetch = () => { void loadOverlay().catch(() => undefined); };
    // Back from a cancelled Checkout the exit offer opens right after the splash: its gift is ready by then.
    if (returnInfo?.outcome === 'cancel') { prefetch(); void preloadExitGift(); }
    if (w.requestIdleCallback) w.requestIdleCallback(prefetch, { timeout: 8000 }); else setTimeout(prefetch, 4000);
  }, [returnInfo]);

  const refreshEntitlement = useCallback(async () => {
    const v = await fetchIsPremium(auth.userID);
    setPremium(v);
    return v;
  }, [auth.userID, setPremium]);

  const evaluate = useCallback(async (signal: RetentionSignal, placement: RetentionPlacement) => {
    try { const r = await api.retentionEvaluate({ signal, placement }); setRetention(r); return r; } catch { return undefined; }
  }, []);

  const recordAbandon = useCallback(async (signal: 'acquisition_paywall_declined' | 'purchase_cancelled') => {
    const r = await evaluate(signal, 'post_paywall_abandon');
    if (r?.eligible && !retentionPromptShownThisSession()) { markRetentionPromptShown(); setRetentionPrompt(true); }
  }, [evaluate]);

  // Mount: entitlement + has_ever_subscribed, then complete a Checkout return.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [premium, ever] = await Promise.all([refreshEntitlement().catch(() => false), fetchHasEverSubscribed(auth.userID).catch(() => false)]);
      if (cancelled) return;
      setEverSubscribed(ever);
      if (!returnInfo) return;
      void navigate({ pathname: window.location.pathname, search: returnInfo.cleanedSearch }, { replace: true });
      const intent: CheckoutIntent | undefined = returnInfo.intent;
      const params = { ...intent?.params, plan: intent?.plan, offer: intent?.offer, paywall_source: 'web' };
      if (returnInfo.outcome === 'cancel') {
        clearIntent();
        track('native_paywall_dismissed', { ...params, dismissal_action: 'checkout_cancelled' });
        void evaluate('purchase_cancelled', 'post_paywall_abandon');
        if (premium) return;
        const variant: PaywallVariant = isPaywallVariant(intent?.variant) ? intent.variant : 'story';
        const startInExit = !ever && intent?.offer !== 'exit_weekly_intro' && !exitOfferShownThisSession();
        keyRef.current += 1;
        setPresentation({ key: keyRef.current, gate: intent?.gate, variant: intent?.gate ? VARIANT_FOR_GATE[intent.gate] : variant, canDismiss: true, startInExit });
        return;
      }
      const ok = premium || await pollUntil(refreshEntitlement, { intervalMs: POLL_INTERVAL_MS, timeoutMs: POLL_TIMEOUT_MS, isCancelled: () => cancelled });
      if (cancelled) return;
      confirmingRef.current = false;
      setConfirming(false);
      clearIntent();
      const pending = pendingRef.current.splice(0);
      if (ok) {
        track('native_purchase_succeeded', params);
        pending.forEach((o) => o.onEntitled?.());
      } else {
        track('native_purchase_failed', { ...params, reason: 'confirmation_timeout' });
        setNotice(t('Payment received. Your access will unlock in a moment.'));
        pending.forEach((o) => o.onDismiss?.());
      }
    })();
    return () => { cancelled = true; };
    // Runs once per signed-in user; returnInfo is read at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.userID]);

  // Re-check after the tab regains focus (purchase finished in another tab, portal cancel…).
  useEffect(() => {
    const onFocus = () => { void refreshEntitlement().catch(() => undefined); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshEntitlement]);

  const dismiss = useCallback(() => {
    const options = presentation?.options;
    setPresentation(undefined);
    options?.onDismiss?.();
  }, [presentation]);

  const entitledFromCard = useCallback(async () => {
    const options = presentation?.options;
    const ok = await refreshEntitlement().catch(() => false);
    setPresentation(undefined);
    if (ok) options?.onEntitled?.(); else options?.onDismiss?.();
  }, [presentation, refreshEntitlement]);

  const api_ = useMemo<PaywallAPI>(() => ({
    isPremium,
    present(gate, options) {
      // Closing a paywall may lead to the exit offer: fetch its gift animation meanwhile.
      void preloadExitGift();
      if (isPremium) { options?.onEntitled?.(); return; }
      // Single source for tap-opened gates (Home, hints, locked CTA, relaunch); the session only
      // tracks premium_gate_hit for proofs that complete the gate request.
      track('premium_gate_hit', { gate, ...options?.params });
      if (confirmingRef.current) { if (options) pendingRef.current.push(options); return; }
      keyRef.current += 1;
      setPresentation({ key: keyRef.current, gate, variant: VARIANT_FOR_GATE[gate], options, canDismiss: !!options?.onDismiss, startInExit: false });
    },
    async openPortal() {
      const { url } = await api.portal({ returnPath: `${location.pathname}${location.search}`, locale });
      window.location.assign(url);
    },
    refreshEntitlement,
  }), [isPremium, refreshEntitlement, location.pathname, location.search, locale]);

  const claim = useCallback(async (placement: RetentionPlacement) => {
    if (claiming) return;
    const params = { paywall_source: 'web', paywall_variant: 'retention', placement, plan: 'annual', product_id: retention?.product_id, currency: prices.data?.currency, price: prices.data?.retentionAnnual.amount };
    track('native_paywall_cta_tapped', params);
    track('native_purchase_started', params);
    setClaiming(true);
    setClaimError(undefined);
    try {
      const r = await startCheckout({ plan: 'annual', offer: 'retention_annual_999', locale, variant: 'retention', params });
      if (r === 'already_premium') { setClaiming(false); setRetentionPrompt(false); await refreshEntitlement(); }
    } catch (error) {
      setClaiming(false);
      setClaimError(t('Purchase failed. Please try again.'));
      track('native_purchase_failed', { ...params, reason: 'checkout_error', error_message: error instanceof Error ? error.message.slice(0, 120) : 'unknown' });
    }
  }, [claiming, retention?.product_id, prices.data, locale, refreshEntitlement, t]);

  const retentionAPI = useMemo<RetentionAPI>(() => ({
    evaluation: isPremium ? undefined : retention,
    evaluate: async (signal, placement) => (isPremium ? undefined : evaluate(signal, placement)),
    claim,
    claiming,
    error: claimError,
  }), [isPremium, retention, evaluate, claim, claiming, claimError]);

  const retentionOffer = retentionPrompt && retention?.eligible && prices.data
    ? { productID: retention.product_id, price: prices.data.retentionAnnual.display, renewal: (prices.data.retentionRenewal ?? prices.data.annual).display }
    : undefined;

  return (
    <PaywallContext.Provider value={api_}>
      <RetentionContext.Provider value={retentionAPI}>
        {children}
        {overlayMounted ? (
          <Suspense fallback={null}>
            <PaywallOverlay
              presentationKey={presentation?.key}
              card={presentation ? {
                variant: presentation.variant,
                episodeID: session.episode?.id ?? (presentation.options?.params?.episode_id as string | undefined),
                canDismiss: presentation.canDismiss,
                isPremium,
                hasEverSubscribed: everSubscribed,
                analyticsContext: presentation.options?.params,
                startInExitOffer: presentation.startInExit,
                onDismiss: dismiss,
                onDecline: () => void recordAbandon('acquisition_paywall_declined'),
                onAlreadyPremium: () => void entitledFromCard(),
              } : undefined}
            />
          </Suspense>
        ) : null}
        {retentionOffer && !presentation ? (
          <Suspense fallback={null}>
            <RetentionOfferSheet offer={retentionOffer} claiming={claiming} error={claimError} onClaim={() => void claim('post_paywall_abandon')} onClose={() => setRetentionPrompt(false)} />
          </Suspense>
        ) : null}
        {confirming || notice ? (
          <div className="pointer-events-none fixed inset-x-0 top-[max(12px,env(safe-area-inset-top))] z-[80] flex justify-center px-4" role="status">
            <button type="button" className="pointer-events-auto rounded-full bg-ink/90 px-4 py-2.5 text-[14px] font-bold text-white shadow-lg" onClick={() => setNotice(undefined)}>
              {confirming ? t('Confirming your purchase…') : notice}
            </button>
          </div>
        ) : null}
      </RetentionContext.Provider>
    </PaywallContext.Provider>
  );
}
